import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import EditForm from '../../../../../../../features/components/Forms/EditForm'
import Field from '../../../../../../../components/Form/Field'
import useDoubleRequestSubmit from '../../../../../../../hooks/formHooks/useDoubleRequestSubmit'

import { usePurifiedFormik } from '../../../../../../../hooks/formHooks/usePurifiedFormik'
import { formatAssetsToAssetGroupUpdate } from '../../formatters'
import { getKeyByValue } from '../../../../../../../helpers/common'
import { getBusinessNameData, getCTAData } from '../HeadlinesSection/helpers'

import {
  clearCreateAssetsBatch,
  clearUpdateAssetGroup,
  createAssetsBatch,
  createAssetsBatchSuccess,
  updateAssetGroup
} from '../../../../../../../modules/actions/assets'
import {
  assetsCreatedDataSelector,
  assetsCreateErrorSelector,
  assetsWasCreatedSelector,
  updateAssetGroupErrorSelector,
  updateAssetGroupWasUpdatedSelector
} from '../../../../../../../modules/selectors/assets'
import { selectedGoogleAdAccountSelector } from '../../../../../../../modules/selectors/adAccounts'

import { googleAssetFieldType, googleAssetType, googleCallToActionEnums } from '../../../../../../../constants/ads'
import { googleAdsCallToActionList } from '../../../../../../../constants/selectLists/googleAdsCallToActionList'

import { getInitialValues, validationSchema } from './fields'
import { CALL_TO_ACTION } from '../../../../../../ReusableFormFields/CallToActionFields/fields'
import { ASSET_GROUP_TYPE, BUSINESS_NAME, NAME, RESOURCE_NAME } from '../../../../fields'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../../constants/changeHistory'

const NameSectionForm = ({ editItemData, isInternalAssetGroup, title, ...formProps }) => {
  const dispatch = useDispatch()

  const { assets, id: assetGroupId, account } = editItemData

  const { id: selectedAdAccountId } = useSelector(selectedGoogleAdAccountSelector)
  const adAccount = account || selectedAdAccountId

  const createdAssetsData = useSelector(assetsCreatedDataSelector)

  const [deleteAssets, setDeleteAssets] = useState([])

  const [editedName, setEditedName] = useState(editItemData[NAME])

  const createNewAssets = useCallback(
    createAssets => {
      dispatch(
        createAssetsBatch({
          account: adAccount,
          operations: createAssets
        })
      )
    },
    [dispatch, adAccount]
  )

  const createAssetsRequest = useCallback(
    values => {
      setEditedName(values[NAME])

      const originalBusinessNameAsset = getBusinessNameData(assets)
      const originalBusinessName = originalBusinessNameAsset?.asset_data.text_asset.text

      const originalCtaAsset = getCTAData(assets)
      const originalCtaEnumValue = originalCtaAsset?.asset_data?.call_to_action_asset?.call_to_action
      const originalCta = getKeyByValue(googleCallToActionEnums, originalCtaEnumValue)

      const selectedCta = values[CALL_TO_ACTION]
      const editedBusinessName = values[BUSINESS_NAME]

      let createAssets = []
      let deleteAssets = []

      if (originalBusinessName !== editedBusinessName) {
        createAssets.push({
          account: adAccount,
          text_asset: { text: editedBusinessName },
          [ASSET_GROUP_TYPE]: googleAssetFieldType.BUSINESS_NAME
        })

        deleteAssets.push({
          [RESOURCE_NAME]: originalBusinessNameAsset[RESOURCE_NAME],
          ...(isInternalAssetGroup && { internal_id: originalBusinessNameAsset.id }),
          action: 'delete'
        })
      }

      if (originalCta !== selectedCta) {
        createAssets.push({
          account: adAccount,
          call_to_action_asset: { call_to_action: googleCallToActionEnums[selectedCta] },
          [ASSET_GROUP_TYPE]: googleAssetFieldType.CALL_TO_ACTION_SELECTION
        })

        deleteAssets.push({
          [RESOURCE_NAME]: originalCtaAsset[RESOURCE_NAME],
          ...(isInternalAssetGroup && { internal_id: originalCtaAsset.id }),
          action: 'delete'
        })
      }

      if (!!createAssets.length) {
        createNewAssets(createAssets)
      } else {
        // skip asset creation and run assetGroup update directly
        // image assets are already created when edit form is submitting
        dispatch(createAssetsBatchSuccess(createAssets))
      }

      setDeleteAssets(deleteAssets)
    },
    [dispatch, assets, createNewAssets, adAccount, isInternalAssetGroup]
  )

  const updateAssetGroupHandler = useCallback(() => {
    // When we create assets "/google/assets/batch/", in response we don't have "field_type",
    // so we use "type_" to distinguish them. businessName = 5, cta = 18
    const createdBusinessNameAssets = createdAssetsData.operations?.filter(
      ({ type_ }) => type_ === googleAssetType.TEXT
    )
    const createdCtaAssets = createdAssetsData.operations?.filter(
      ({ type_ }) => type_ === googleAssetType.CALL_TO_ACTION
    )

    const formattedBusinessNameCreateAssets = formatAssetsToAssetGroupUpdate(
      createdBusinessNameAssets,
      googleAssetFieldType.BUSINESS_NAME,
      assetGroupId
    )

    const formattedCtaCreateAssets = formatAssetsToAssetGroupUpdate(
      createdCtaAssets,
      googleAssetFieldType.CALL_TO_ACTION_SELECTION,
      assetGroupId
    )

    dispatch(
      updateAssetGroup(
        {
          account: adAccount,
          // All formatted assets. We format create and delete assets separately
          assets: [...deleteAssets, ...formattedBusinessNameCreateAssets, ...formattedCtaCreateAssets],
          [NAME]: editedName,
          ...(isInternalAssetGroup && { internal: true }),
          meta: {
            [HISTORY_META_EDIT_SECTION]: title
          }
        },
        assetGroupId
      )
    )
  }, [dispatch, adAccount, deleteAssets, createdAssetsData, assetGroupId, editedName, isInternalAssetGroup, title])

  const clearSubmitHandler = useCallback(() => {
    dispatch(clearCreateAssetsBatch())
    dispatch(clearUpdateAssetGroup())
  }, [dispatch])

  const batchAssetsRequest = useMemo(
    () => ({
      requestHandler: createAssetsRequest,
      errorSelector: assetsCreateErrorSelector,
      successSelector: assetsWasCreatedSelector,
      dataSelector: assetsCreatedDataSelector
    }),
    [createAssetsRequest]
  )

  const updateAssetGroupRequest = useMemo(
    () => ({
      requestHandler: updateAssetGroupHandler,
      errorSelector: updateAssetGroupErrorSelector,
      successSelector: updateAssetGroupWasUpdatedSelector
    }),
    [updateAssetGroupHandler]
  )

  const requests = useMemo(
    () => [batchAssetsRequest, updateAssetGroupRequest],
    [batchAssetsRequest, updateAssetGroupRequest]
  )

  const { onSubmit, onClearSubmit, isLoadingSelector, errorSelector, successSubmit } = useDoubleRequestSubmit({
    requests,
    clearSubmitHandler
  })

  const initialValues = useMemo(() => getInitialValues(editItemData), [editItemData])

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit,
    validationSchema
  })

  const purifiedFormik = usePurifiedFormik(formik)

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      isLoadingSelector={isLoadingSelector}
      errorSelector={errorSelector}
      successSubmit={successSubmit}
      clearEditItem={onClearSubmit}
      {...formProps}
    >
      <Field formik={formik} name={NAME} placeholder="Name" autoComplete="off" />
      <Field formik={formik} name={BUSINESS_NAME} placeholder="Business name" autoComplete="off" />
      <Field placeholder="Call To Action" formik={formik} name={CALL_TO_ACTION} options={googleAdsCallToActionList} />
    </EditForm>
  )
}

export default NameSectionForm
